import { formatDistance } from "date-fns"
import { AUTH_FAILURE_LABEL, is_auth_failure, is_on_demand_connection, is_recent_sync } from "../sync_health"

interface IntegrationLogoProps {
  src: string
  alt: string
}

export function IntegrationLogo({ src, alt }: IntegrationLogoProps) {
  return (
    <div class="w-16 flex justify-center">
      <img src={src} alt={alt} class="max-h-8 object-contain" />
    </div>
  )
}

type LastSyncJob = { id: string; created_at: string; error?: string | null }

interface SyncStatusDisplayProps {
  lastSyncJob?: LastSyncJob | null
  integrationName?: string
}

export interface SyncStatusDetail {
  /**
   * Outcome of the last sync. The settings app shows only the reassuring half —
   * "Syncing normally" once the connection has run inside the generous window —
   * never a raw error, which means nothing to the person looking at it here.
   */
  outcome: { label: string; isError: boolean } | null
  lastSync: { label: string; isError: boolean }
}

/**
 * Sync detail for one connection. Everyone in the brand settings app — including
 * system admins sitting in it — sees when the sync last ran. Raw errors, staleness
 * and job internals are operational signals and live in admin. A rejected credential
 * is the exception: it is the brand's to fix, and hiding it hid five months of a
 * dead mailbox behind a green "Syncing normally".
 */
export function syncStatusDetail(
  lastSyncJob: LastSyncJob | null | undefined,
  integrationName: string | undefined,
  now: Date = new Date()
): SyncStatusDetail {
  if (is_on_demand_connection(integrationName)) {
    const lastSync = lastSyncJob
      ? {
          label: `Last sync: ${formatDistance(new Date(lastSyncJob.created_at), now, { addSuffix: true })}`,
          isError: false,
        }
      : { label: "Synced", isError: false }
    return { outcome: { label: "Syncing normally", isError: false }, lastSync }
  }

  if (!lastSyncJob) {
    return {
      outcome: null,
      lastSync: { label: "Not synced yet", isError: false },
    }
  }

  const syncDate = new Date(lastSyncJob.created_at)
  // formatDistance rather than formatDistanceToNow so `now` can be pinned in tests.
  const lastSyncLabel = `Last sync: ${formatDistance(syncDate, now, { addSuffix: true })}`
  // A credential the provider has rejected still gets its scheduled job, which fails in
  // milliseconds — so recency keeps saying "normal" while nothing is ingested. This is the one
  // error the brand can actually act on, so it is the one error they get to see.
  if (is_auth_failure(lastSyncJob.error)) {
    return {
      outcome: { label: AUTH_FAILURE_LABEL, isError: true },
      lastSync: { label: lastSyncLabel, isError: false },
    }
  }
  // Feeds are event-driven, so a quiet week is normal operation. Say so rather than leaving a
  // bare date to interpret — see `HEALTHY_SYNC_WINDOW_DAYS` for the window.
  const outcome = is_recent_sync(syncDate, now) ? { label: "Syncing normally", isError: false } : null
  return { outcome, lastSync: { label: lastSyncLabel, isError: false } }
}

export function SyncStatusDisplay({ lastSyncJob, integrationName }: SyncStatusDisplayProps) {
  const status = syncStatusDetail(lastSyncJob, integrationName)

  return (
    <div class="space-y-1">
      {status.outcome && (
        <p class={`text-sm font-medium ${status.outcome.isError ? "text-red-500" : "text-green-600"}`}>
          {status.outcome.label}
        </p>
      )}
      <p class={`text-sm ${status.lastSync.isError ? "text-red-500" : "text-gray-500"}`}>{status.lastSync.label}</p>
      {status.outcome?.label === AUTH_FAILURE_LABEL && (
        <p class="text-sm text-gray-500">
          The connection signed out, so nothing new has come in since. Sign in again to restore it.
        </p>
      )}
    </div>
  )
}

interface SyncStatusRowProps {
  logo: string
  name: string
  username: string
  lastSyncJob?: LastSyncJob | null
  integrationName?: string
  children?: React.ReactNode
}

export function SyncStatusRow({ logo, name, username, lastSyncJob, integrationName, children }: SyncStatusRowProps) {
  return (
    <div class="flex items-center gap-4 py-1 border-b border-gray-100 last:border-b-0">
      <IntegrationLogo src={logo} alt={name} />
      <div class="flex-1 min-w-0">
        <div class="text-sm">{username}</div>
        <SyncStatusDisplay lastSyncJob={lastSyncJob} integrationName={integrationName} />
      </div>
      {children}
    </div>
  )
}
